<template>
  <KpiCard
  style="gap: 0px;"
    :icon="icon"
    :title="title"
    :iconType="iconType"
    :titleColor="titleColor"
    :iconColor="iconColor"
    :iconBgColor="iconBgColor"
    :iconBG="true"
  >
    <template #value>
      <div class="main-container">
        <div style="height: 100%;">
          <div class="reading-period">
            <div class="date-range">
              <span class="date-item">
                <span class="date-label">Início da leitura</span>
                <span class="date-value">{{ (readingStartDate) }}</span>
              </span>
              <span class="date-item">
                <span class="date-label">Fim da leitura</span>
                <span class="date-value">{{ (readingEndDate) }}</span>
              </span>
              <span class="date-item days-elapsed">
                <span class="date-label">Dias percorridos</span>
                <span class="date-value">{{ daysElapsed }}</span>
              </span>
            </div>
          </div>

          <div class="bar-wrapper">
            <!-- Barra de fundo - representa 100% da meta ou o consumo previsto (o que for maior) -->
            <div class="bar-base">
              <!-- Barra dividida em duas partes: normal e excedente -->
              <div class="bar-fill-container">
                <!-- Parte dentro da meta (até o limite da meta) -->
                <div
                  class="bar-fill bar-fill-normal"
                  :style="{ 
                    width: normalFillWidth,
                    borderRadius: '12px 12px 12px 12px',
                    zIndex: 3
                  }"
                ></div>
                
                <!-- Parte excedente (acima da meta) -->
                <div
                  v-if="Number(measured) > Number(goal)"
                  class="bar-fill bar-fill-excess"
                  :style="{ 
                    width: excessFillWidth, 
                    left: normalFillWidth, 
                    borderRadius: '0 12px 12px 0',
                    zIndex: 2
                  }"
                ></div>
                
                <!-- Parte prevista (forecast, entre measured e estimated) -->
                <div
                  v-if="Number(estimated) > Number(measured)"
                  class="bar-fill bar-fill-forecast"
                  :style="{
                    width: forecastFillWidth,
                    left: forecastLeftOverlap,
                    borderRadius: '12px',
                    zIndex: 1
                  }"
                ></div>

              </div>
              
              <!-- Linha: Meta (dentro da barra) - apenas exibida quando estimated > goal -->
              <div 
                class="marker marker-goal" 
                :style="{ left: (Math.max(goalPercentage - 1, 0)) + '%', zIndex: 4 }"
              >
              </div>
            </div>
    
            <!-- Scale marks -->
            <div class="scale-marks">
              <div 
                v-for="mark in calculatedScaleMarks" 
                :key="mark.value" 
                :style="{ left: mark.percentage + '%' }"
              >
                <div class="mark-text">{{ formatScaleValue(mark.value) }}</div>
              </div>
            </div>
          </div>

          <!-- Status indicator for consumption vs goal -->
          <div class="status-indicator">
            <!-- Current consumption already exceeds goal -->
            <div v-if="Number(measured) > Number(goal)" class="status-icon status-error">
              <i class="material-icons">error</i>
            </div>
            <div v-else-if="forecastExceedsGoal" class="status-icon status-warning">
              <i class="material-icons">warning</i>
            </div>
            <div v-else class="status-icon status-good">
              <i class="material-icons">check_circle</i>
            </div>
            <div class="status-message">
              <span v-if="Number(measured) > Number(goal)" class="error-message">
                Meta de consumo excedida!
              </span>
              <span v-else-if="forecastExceedsGoal">
                Previsão acima da meta!
                <span class="percentage-over">({{ forecastExcessPercentage }}% acima)</span>
              </span>
              <span v-else>Previsão dentro da meta</span>
            </div>
          </div>

          <!-- Horizontal legends instead of positioned labels -->
          <div class="legend-container">
            <div class="legend-col">
              <div class="legend-item current-legend">
                <div class="legend-color current-color"></div>
                <div class="legend-content">
                  <div class="legend-text">Consumo total</div>
                  <div class="legend-value">{{ formatNumber(adjustedMeasured) }} {{ unitSuffix }}</div>
                </div>
              </div>
              <div class="legend-item goal-legend">
                <div class="legend-color goal-color"></div>
                <div class="legend-content">
                  <div class="legend-text">Limite de consumo</div>
                  <div class="legend-value">{{ formatNumberNoDecimals(adjustedGoal) }} {{ unitSuffix }}</div>
                </div>
              </div>
            </div>
            <div class="legend-col">
              <div class="legend-item excess-legend" v-if="Number(adjustedMeasured) > Number(adjustedGoal)">
                <div class="legend-color excess-color"></div>
                <div class="legend-content">
                  <div class="legend-text">Consumo excedente</div>
                  <div class="legend-value">{{ formatNumber(Number(adjustedMeasured) - Number(adjustedGoal)) }} {{ unitSuffix }}</div>
                </div>
              </div>
              <div
                class="legend-item forecast-legend"
                v-if="Number(adjustedEstimated) !== Number(adjustedMeasured)"
                title="Consumo previsto = consumo atual + projeção até o fim do período"
              >
                <div class="legend-color forecast-color"></div>
                <div class="legend-content">
                  <div class="legend-text">Consumo previsto</div>
                  <div class="legend-value">{{ formatNumber(adjustedEstimated) }} {{ unitSuffix }}</div>
                </div>
              </div>
            </div>
          </div>
    
          <div class="footer">
          </div>
        </div>
      </div>
    </template>
  </KpiCard>
</template>

<script>
import Chart from 'chart.js';
import KpiCard from '@/pages/newDashboard/Widgets/KpiCard.vue';
import store from '@/store/index'

export default {
  name: 'DoughnutGoalCard',
  components: {
    KpiCard
  },
  props: {
    // Props do KpiCard que vamos passar adiante
    title: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      required: true
    },
    iconType: {
      type: String,
      default: 'material',
      validator: value => ['material', 'image'].includes(value)
    },
    titleColor: {
      type: String,
      default: '#63A9FF'
    },
    iconColor: {
      type: String,
      default: '#4092FF'
    },
    iconBgColor: {
      type: String,
      default: '#D0E5FF'
    },
    
    goal: {
      type: Number,
      required: true
    },
    measured: {
      type: Number,
      required: true
    },
    estimated: {
      type: Number,
      required: true
    },
    readingStartDate: {
      type: [String, Date],
      default: null
    },
    readingEndDate: {
      type: [String, Date],
      default: null
    }
  },
  data() {
    return {
      chart: null
    };
  },
  watch: {
    total() {
      this.updateChart();
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    forecastExceedsGoal() {
      return Number(this.estimated) > Number(this.goal);
    },
    barMax() {
      // When goal is larger than forecast, we want the bar to show the full goal
      // Otherwise, use the max of the three values
      const goalValue = Number(this.goal);
      const measuredValue = Number(this.measured);
      const estimatedValue = Number(this.estimated);
      
      // If goal is larger than estimated, use goal as max
      // This ensures the bar represents the full goal when goal > estimated
      if (goalValue > estimatedValue) {
        return goalValue * 2;
      } else {
        // Otherwise use the largest value
        return Math.max(goalValue, measuredValue, estimatedValue) * 2;
      }
    },
    consumptionPercentage() {
      // Calculate the percentage of measured consumption relative to the bar max
      const measuredValue = Number(this.measured);
      const percentage = Math.min((measuredValue / this.barMax) * 100, 100).toFixed(1);
      return percentage;
    },
    forecastPercentage() {
      // Forecast as percentage of the maximum bar value
      const estimatedValue = Number(this.estimated);
      const percentage = Math.min((estimatedValue / this.barMax) * 100, 100).toFixed(1);
      return percentage;
    },
    goalPercentage() {
      // Goal as percentage of the maximum bar value (only relevant if estimated > goal)
      const goalValue = Number(this.goal);
      const percentage = Math.min((goalValue / this.barMax) * 100, 100).toFixed(1);
      return percentage;
    },
    normalFillWidth() {
      // For correct visual representation: 
      // If consumption is below goal, calculate percentage relative to bar max
      const measuredValue = Number(this.measured);
      const goalValue = Number(this.goal);
      
      if (measuredValue <= goalValue) {
        // Use direct division instead of consumptionPercentage to avoid string conversion issues
        const percentage = (measuredValue / this.barMax) * 100;
        return percentage + '%';
      } else {
        // If consumption exceeds goal, show fill up to the goal value only
        // This is the part that is WITHIN the goal (will be in blue-green)
        const percentage = (goalValue / this.barMax) * 100;
        return percentage + '%';
      }
    },
    excessFillWidth() {
      // Only returns a value if consumption exceeds goal
      const measuredValue = Number(this.measured);
      const goalValue = Number(this.goal);
      
      if (measuredValue > goalValue) {
        const excessAmount = measuredValue - goalValue;
        const excessPercentage = (excessAmount / this.barMax) * 100;
        return excessPercentage + '%';
      }
      return '0%';
    },
    forecastFillWidth() {
      // Returns the width for the forecast bar (between measured and estimated, only if estimated > measured and estimated <= goal)
      const measuredValue = Number(this.measured);
      const estimatedValue = Number(this.estimated);
      const goalValue = Number(this.goal);
      const forecastAmount = estimatedValue - measuredValue;
      const forecastPercentage = (forecastAmount / this.barMax) * 100;
      return forecastPercentage + '%';
    },
    forecastLeftOverlap() {
      const measuredValue = Number(this.measured);
      const barMax = this.barMax;
      let left = (measuredValue / barMax) * 100;
      if (left < 0) left = 0;
      return Math.max(left - 1.5, 0) + '%';
    },
    calculatedScaleMarks() {
      const result = [];
      const maxValue = this.barMax;
      const numDivisions = 6; // 6 divisões = 7 marcas (0, 1/6, 2/6, ..., 6/6)
      if (maxValue > 0) {
        for (let i = 0; i <= numDivisions; i++) {
          const value = (i * maxValue) / numDivisions;
          const percentage = (value / maxValue) * 100;
          result.push({
            value,
            percentage
          });
        }
      }
      // Adicione marcadores especiais se necessário (goal, estimated), mantendo a lógica anterior
      // ... (restante da lógica para goal/estimated, se quiser manter)
      return result;
    },
    chartData() {
      const excedente = Math.max(this.measured + (this.estimated - this.measured) - this.goal, 0);
      const estimado = Math.max(this.estimated - this.measured, 0);
      const capacidadeRestante = Math.max(this.goal - (this.measured + estimado), 0);

      if (excedente > 0) {
        return {
          labels: ['Medido', 'Previsto', 'Excedente'],
          values: [this.measured, estimado, excedente],
          colors: ['#69c871', '#FFC23D', '#FF7F00']
        };
      }

      return {
        labels: ['Medido', 'Previsto', 'Capacidade'],
        values: [this.measured, estimado, capacidadeRestante],
        colors: ['#69c871', '#FFC23D', '#d3d3d3']
      };
    },
    daysElapsed() {
      if (!this.readingStartDate) return null;
      
      try {
        // Handle string date in DD/MM/YYYY format
        let startDate;
        
        if (typeof this.readingStartDate === 'object') {
          startDate = this.readingStartDate;
        } else if (typeof this.readingStartDate === 'string') {
          // Check if format is DD/MM/YYYY
          if (this.readingStartDate.includes('/')) {
            const parts = this.readingStartDate.split('/');
            if (parts.length === 3) {
              // Create date with parts[0]=day, parts[1]=month (0-based), parts[2]=year
              startDate = new Date(parts[2], parts[1] - 1, parts[0]);
            } else {
              throw new Error("Invalid date format");
            }
          } else {
            // Try standard JavaScript date parsing
            startDate = new Date(this.readingStartDate);
          }
        } else {
          throw new Error("Invalid date type");
        }
        
        // Reset time component to ensure exact day calculation
        startDate.setHours(0, 0, 0, 0);
        
        // Get today's date with time reset to midnight
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Calculate the difference in milliseconds
        const diffTime = today.getTime() - startDate.getTime();
        
        // Convert to days (milliseconds to days)
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        // Return 0 if negative (should not happen with proper dates)
        return Math.max(0, diffDays);
      } catch (e) {
        console.error("Error calculating days elapsed:", e);
        return 0;
      }
    },
    forecastExcessPercentage() {
      if (!this.forecastExceedsGoal) return null;
      
      const measuredValue = Number(this.measured);
      const estimatedValue = Number(this.estimated);
      const goalValue = Number(this.goal);
      
      const excessAmount = estimatedValue - goalValue;
      const excessPercentage = (excessAmount / goalValue) * 100;
      
      return excessPercentage.toFixed(1);
    },
    unitMeasure() {
      return store.getters.unitMeasure;
    },
    adjustedGoal() {
      return this.convertUnits(this.goal, this.unitMeasure);
    },
    adjustedMeasured() {
      return this.convertUnits(this.measured, this.unitMeasure);
    },
    adjustedEstimated() {
      return this.convertUnits(this.estimated, this.unitMeasure);
    },
    unitSuffix() {
      return this.unitMeasure === 'liters' ? 'L' : 'm³';
    }
  },
  methods: {
    convertUnits(value, unitMeasure) {
      return unitMeasure === 'liters' ? value * 1000 : value;
    },
    formatNumber(number) {
      return Number(number).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
    formatNumberNoDecimals(number) {
      return Number(number).toLocaleString('pt-BR', {minimumFractionDigits: 0, maximumFractionDigits: 2});
    },
    formatDate(date) {
      if (!date) return '';
      
      // Handle both string and Date object inputs
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      
      // Check if valid date
      if (isNaN(dateObj.getTime())) return '';
      
      // Format as DD/MM/YYYY
      const day = dateObj.getDate().toString().padStart(2, '0');
      const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
      const year = dateObj.getFullYear();
      
      return `${day}/${month}/${year}`;
    },
    formatScaleValue(value) {
      // Format scale numbers for better readability
      const roundedValue = Math.round(value);
      if (roundedValue >= 1000) {
        return Math.round(roundedValue / 100) / 10 + 'k';
      } else {
        return roundedValue;
      }
    },
  }
};
</script>

<style scoped>
.header {
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 12px;
}

/* Status indicator styles */
.status-indicator {
  display: flex;
  padding: 10px;
  margin-top: 30px;
  margin-bottom: 12px;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 10px;
  background: #EAF3FF;
}

.status-icon {
  display: flex;
  align-items: center;
}

.status-icon i {
  font-size: 24px;
}

.status-good i {
  color: #002E71;
}

.status-warning i {
  color: #FF7F00;
}

.status-error i {
  color: #f44336;
}

.status-message {
  font-size: 0.85rem;
  font-weight: 500;
}

.status-good + .status-message {
  color: #002E71;
}

.status-warning + .status-message {
  color: #FF7F00;
}

.percentage-over {
  font-family: Manrope;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  color: #545454;
}

.status-error + .status-message,
.error-message {
  color: #c62828;
  font-weight: 600;
}

.bar-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  position: relative;
}

.bar-base {
  background: #E0EFFF;
  border-radius: 12px;
  width: 100%;
  height: 12px;
  position: relative;
  overflow: visible; /* Changed from hidden to visible */
  z-index: 6;
}

.bar-fill-container {
  display: flex;
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

.bar-fill {
  height: 100%;
  position: absolute;
}

.bar-fill-normal,
.bar-fill-forecast,
.bar-fill-excess {
  border-radius: 0 12px 12px 0;
}

.bar-fill-normal {
  background: #42A5F5;
  left: 0;
  z-index: 3;
}

.bar-fill-forecast {
  background: #FFC23D;
  z-index: 1;
}

.bar-fill-excess {
  background: #FF7F00;
  z-index: 3;
}

.marker {
  position: absolute;
  top: -3px;
  width: 5px;
  height: 20px;
  z-index: 2; 
  flex-shrink: 0;
}

.marker-goal {
  background-color: #002E71;
  border-radius: 5px;
}

.scale-marks {
  position: relative;
  width: 100%;
  margin-top: 4px;
  left: 0;
}

.scale-marks > div {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  text-align: center;
  min-width: 24px;
}

.mark-text {
  color: #545454;
  font-family: Manrope;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  white-space: nowrap;
}

/* Reading period styles */
.reading-period {
  margin-top: 15px;
  margin-bottom: 35px;
  width: 100%;
}

.date-range {
  display: flex;
  align-items: center;
  gap: 22px;
  align-self: stretch;
}

.date-item {
  display: flex;
  flex-direction: column;
  align-items: start;
  flex: 1;
  text-align: start;
  min-width: 0; /* Allows flex items to shrink below min-content */
}

.date-label {
  color: #1C1C1C;
  font-family: Manrope;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.date-value {
  color:#1C1C1C;
  font-family: Manrope;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.days-elapsed {
  margin-top: 0;
}

/* New horizontal legend styles */
.legend-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 40px; /* Espaço entre as colunas */
  width: 100%;
}

.legend-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.legend-item {
  display: flex;
  align-items: flex-start;
}

.legend-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.legend-text {
  color: #545454;
  font-size: 13px;
  font-weight: 500;
}

.legend-value {
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
}

.current-legend .legend-value { color: #42A5F5; }
.excess-legend .legend-value { color: #FF7F00; }
.goal-legend .legend-value { color: #002E71; }
.forecast-legend .legend-value { color: #FFC23D; }

.legend-color {
  width: 4px;
  height: 32px;
  border-radius: 2px;
  margin-right: 6px;
}

.current-color {
  background: #42A5F5;
}

.excess-color {
  background: #FF7F00;
}

.goal-color {
  background: #002E71;
}

.forecast-color {
  background: #FFC23D;
}

.info {
  font-size: 0.9rem;
  color: #333;
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
}

.consumed {
  color: #42A5F5;
  font-weight: bold;
  font-size: 1.1rem;
}

.footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 8px;
}

.goal-footer-label {
  margin-right: 8px;
  font-size: 0.9rem;
  color: #555;
}

.goal-value {
  color: #002E71;
  font-size: 1.3rem;
  font-weight: bold;
}

.main-container {
  position: static;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

::v-deep .kpi-card {
  padding: 0 !important;
  gap: 0 !important;
  align-items: stretch !important;
}

</style>
